import React, { useCallback, useEffect, useRef } from "react";
import { TicketPurchaseApi } from "../../services/TicketPurchaseApi";
import type { TicketPurchaseState } from "../../store/ephemeral";
import { ephemeralStore, TICKET_PURCHASE_DEFAULT, ticketPurchaseSlice, useEphemeralSelector } from "../../store/ephemeral";
import { pricingPrefsSlice } from "../../store/persistence";
import { persistentSliceStore } from "../../store/persistence/runtime/PersistentSliceStore";
import { SheetModal } from "../shared/SheetModal";
import { cancelPendingPurchasePayment } from "./payrexxReturn.tools";
import { TicketBookingConfirmation } from "./TicketBookingConfirmation";
import { TicketOrderSummary } from "./TicketOrderSummary";
import { TicketPayment } from "./TicketPayment";
import type { TicketPurchaseModalProps } from "./ticketPurchase.types";
import { buildCffSegmentDefaults } from "./ticketPurchaseSegments.tools";
import { TicketPurchaseStepper } from "./TicketPurchaseStepper";
import { TicketSegmentSelection } from "./TicketSegmentSelection";

/**
 * Full-screen mobile / centered desktop modal for the ticket purchase wizard.
 *
 * @param props - Modal props.
 * @returns The purchase modal or null when closed.
 */
export const TicketPurchaseModal: React.FC<TicketPurchaseModalProps> = ( {
    isOpen,
    onClose,
    isDarkUi,
    itinerary,
} ) => {
    const ticketState = useEphemeralSelector( ticketPurchaseSlice, ( s: TicketPurchaseState ) => s );
    const currentStep = ticketState.step;
    const canClose = ticketState.canClose;
    const paymentStatus = ticketState.paymentStatus;
    const purchaseAttemptId = ticketState.purchaseAttemptId ?? ticketState.prebookingResponse?.purchaseAttemptId ?? null;

    const loadSegments = useCallback( async( it: typeof itinerary ) => {
        if ( it === null ) {
            return;
        }

        try {
            const response = await TicketPurchaseApi.postPurchaseSegments( it );
            const pricingPrefs = persistentSliceStore.peek( pricingPrefsSlice );
            const defaultClass = pricingPrefs.categoryOfService === "FIRST_CLASS" ? "1" : "2";
            const { segmentClass, skipSegment } = buildCffSegmentDefaults( it, defaultClass );

            ephemeralStore.setValue( ticketPurchaseSlice, ( prev ): TicketPurchaseState => ( {
                ...prev,
                "itinerary":             it,
                "step":                  "selection",
                "selectionSegmentIndex": 0,
                "selectedOffers":        {},
                segmentClass,
                skipSegment,
                "error":                 null,
            } ) );

            void response;
        } catch( error ) {
            ephemeralStore.setValue( ticketPurchaseSlice, ( prev ): TicketPurchaseState => ( {
                ...prev,
                "itinerary": it,
                "error":     error instanceof Error ? error.message : "Erreur de chargement",
            } ) );
        }
    }, [] );

    /**
     * Itinerary the segments were already loaded for. Prevents the reload from
     * re-triggering when `confirm` clears `purchaseAttemptId` — otherwise the
     * wizard would bounce back to `selection` on every payment attempt.
     */
    const loadedItineraryRef = useRef<typeof itinerary>( null );

    /*
     * Saved-method lookup: the « Paiement en un clic » opt-in signal lives
     * server-side (`user_payment_method`). When a method exists and the
     * shopper has not picked a mode yet (selector click or resume restore),
     * the summary selector defaults to `direct`. A lookup failure must not
     * block the purchase — the standard mode stays selected.
     */
    useEffect( () => {
        if ( isOpen !== true ) {
            return;
        }

        let cancelled = false;

        void TicketPurchaseApi.getPurchasePaymentMethod().
            then( ( method ) => {
                if ( cancelled || method === null ) {
                    return;
                }

                ephemeralStore.setValue( ticketPurchaseSlice, ( prev ): TicketPurchaseState => ( {
                    ...prev,
                    "checkoutMode": prev.checkoutModeTouched ? prev.checkoutMode : "direct",
                } ) );
            } ).
            catch( () => { /* The saved-method lookup is a display defaulting hint — failures keep `standard`. */ } );

        return () => {
            cancelled = true;
        };
    }, [ isOpen ] );

    useEffect( () => {
        /*
         * A Payrexx resume reopens the modal with the purchase state already
         * restored (attempt id set, step `payment`/`confirmation`); reloading
         * segments here would reset the wizard back to `selection`.
         */
        if ( isOpen !== true || itinerary === null ) {
            loadedItineraryRef.current = null;

            return;
        }

        if ( purchaseAttemptId !== null || loadedItineraryRef.current === itinerary ) {
            /*
             * Segments are already loaded for this itinerary — either just now or
             * restored by the Payrexx resume. Record it either way so a later
             * `purchaseAttemptId` reset (new prebook) does not bounce the wizard
             * back to the selection step.
             */
            loadedItineraryRef.current = itinerary;

            return;
        }

        loadedItineraryRef.current = itinerary;
        void loadSegments( itinerary );
    }, [ isOpen, itinerary, loadSegments, purchaseAttemptId ] );

    const handleClose = useCallback( () => {
        if ( canClose === false ) {
            return;
        }

        /*
         * Closing while a Payrexx checkout is open aborts the payment: the backend
         * deletes the still-waiting gateway so it can never be paid silently.
         * An `authorized` reservation (booking not started yet) is released the
         * same way so the blocked amount is freed immediately.
         */
        if (
            purchaseAttemptId !== null &&
            ( paymentStatus === "awaiting_return" || ( paymentStatus === "authorized" && ticketState.bookingStatus === "idle" ) )
        ) {
            void cancelPendingPurchasePayment( purchaseAttemptId );
        }

        ephemeralStore.setValue( ticketPurchaseSlice, TICKET_PURCHASE_DEFAULT );
        onClose();
    }, [ canClose, onClose, paymentStatus, purchaseAttemptId, ticketState.bookingStatus ] );

    const renderStep = useCallback( () => {
        switch ( currentStep ) {
            case "selection":
                return <TicketSegmentSelection isDarkUi={isDarkUi} />;

            case "summary":
                return <TicketOrderSummary isDarkUi={isDarkUi} />;

            case "payment":
                return <TicketPayment isDarkUi={isDarkUi} />;

            case "confirmation":
                return (
                    <TicketBookingConfirmation
                        isDarkUi={isDarkUi}
                        onClose={handleClose}
                    />
                );

            default:
                return null;
        }
    }, [ currentStep, handleClose, isDarkUi ] );

    if ( !isOpen ) {
        return null;
    }

    return (
        <SheetModal
            canClose={canClose}
            headerExtra={<TicketPurchaseStepper isDarkUi={isDarkUi} />}
            isDarkUi={isDarkUi}
            isOpen={isOpen}
            onClose={handleClose}
            title="Achat de billet"
        >
            {renderStep()}
        </SheetModal>
    );
};
